// Client-side feedback context — the diagnostic payload attached to a submitted report.
// Mirrors the `FeedbackClientContext` wire contract in @parsleyai/functions (the packages
// don't share a types module, so the shapes are kept in sync by hand). Identity
// (uid/email/displayName) is intentionally absent: the callable stamps it from the auth
// token, never the client (spec D2).

// Closed set — the two report kinds. Mirrors the functions-side `FeedbackType`; the string
// values are the wire contract and must match exactly (no-magic-strings rule).
export enum FeedbackType {
  Bug = 'bug',
  Feedback = 'feedback'
}

export interface FeedbackClientContext {
  route?: string
  appVersion?: string
  gitSha?: string
  userAgent?: string
  viewport?: { width: number; height: number }
  isMobile?: boolean
  theme?: string
  planContext?: { phase?: string; planId?: string }
}

interface GatherInput {
  /** Current route (pathname + search) — from `useLocation`. */
  route?: string
  /** `'light'` | `'dark'` — from `useTheme`. */
  theme?: string
  /** Active meal-plan generation state, if any — from `usePlanGen`. */
  planContext?: { phase?: string; planId?: string }
}

/**
 * Assembles best-effort diagnostic context for a feedback report. Never throws — a failing or missing
 * read is simply omitted, so gathering context can't block a submit.
 */
export function gatherFeedbackContext(input: GatherInput = {}): FeedbackClientContext {
  const ctx: FeedbackClientContext = {}
  if (input.route) ctx.route = input.route
  if (input.theme) ctx.theme = input.theme
  if (input.planContext && (input.planContext.phase || input.planContext.planId)) ctx.planContext = input.planContext

  try {
    const version = import.meta.env.VITE_APP_VERSION
    if (version) ctx.appVersion = version
    const sha = import.meta.env.VITE_GIT_SHA
    if (sha) ctx.gitSha = sha
    if (typeof navigator !== 'undefined' && navigator.userAgent) ctx.userAgent = navigator.userAgent
    if (typeof window !== 'undefined') {
      ctx.viewport = { width: window.innerWidth, height: window.innerHeight }
      ctx.isMobile = window.innerWidth < 768
    }
  } catch {
    // Best-effort — return whatever we managed to gather (spec D4).
  }

  return ctx
}
